"use client";

import { FeatureList } from "../FeatureList";
import { OrderSummaryCard } from "../OrderSummaryCard";
import { RemediationPlanCard } from "../RemediationPlanCard";
import { YearChoiceList } from "../YearChoiceList";
import {
  ACCOUNTING_REMEDIATION_EXTRA_TRANSACTION_PRICE,
  FINANCIAL_YEAR_OPTIONS,
  formatHKD,
  type PackageSummary,
  type RemediationPlan,
} from "../pricing";

/**
 * The accounting and audit "how many past financial years?" screens, which are
 * the same screen with different copy and a different rate.
 *
 * Both are priced per financial year up to 100 transactions, and both carry the
 * transaction-overage note — deliberately excluded from the total, since the
 * copy says the top-up is quoted after review.
 *
 * The company-secretary version stays separate: it has no transaction cap, no
 * overage note and a single full-width Next rather than this Skip/Next pair.
 */
export function ManageRemediationScreen({
  heading,
  intro,
  legend,
  name,
  plan,
  years,
  onYearsChange,
  onNext,
  onSkip,
  summary,
}: {
  heading: string;
  /** A node, so a caller can supply a sub-question and guidance copy. */
  intro: React.ReactNode;
  legend: string;
  /** Radio group name — must differ per screen or the two would share state. */
  name: string;
  plan: RemediationPlan;
  years: string | null;
  onYearsChange: (value: string) => void;
  onNext: () => void;
  onSkip: () => void;
  summary: PackageSummary;
}) {
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (years) onNext();
      }}
      className="mx-auto w-full max-w-[1250px] px-4 pb-16 pt-8 sm:px-6 sm:pb-24 sm:pt-12 lg:pt-[31px]"
    >
      <div className="flex flex-col gap-8 lg:flex-row lg:items-start">
        <div className="flex min-w-0 flex-1 flex-col items-start gap-6 sm:gap-8">
          <h1 className="w-full font-suisse-intl-bold text-[26px] leading-tight text-white sm:text-4xl md:text-5xl lg:text-[53px] lg:leading-[71px]">
            {heading}
          </h1>

          {intro}

          <YearChoiceList
            legend={legend}
            name={name}
            options={FINANCIAL_YEAR_OPTIONS}
            value={years}
            onChange={onYearsChange}
          />

          <div className="flex w-full flex-col items-start gap-6 sm:gap-8">
            <h2 className="w-full font-suisse-works text-[28px] leading-tight text-white [text-shadow:0px_4px_24px_rgba(0,0,0,0.05)] sm:text-[40px] sm:leading-[64px] lg:text-5xl">
              Your remediation plan
            </h2>

            <RemediationPlanCard plan={plan} />

            <div className="flex w-full flex-col items-start gap-2">
              <h3 className="font-suisse-intl-bold text-xl leading-snug text-white sm:text-2xl sm:leading-[38.4px]">
                Have more than 100 transactions for the financial year?
              </h3>
              <p className="font-suisse-intl-medium text-base leading-relaxed text-white sm:text-lg sm:leading-[28.8px]">
                Additional transactions are charged at{" "}
                {formatHKD(ACCOUNTING_REMEDIATION_EXTRA_TRANSACTION_PRICE)} each. We will let you
                know the amount you need to top up after our accountants have reviewed your past
                financial years.
              </p>
            </div>

            <FeatureList title="What's included" items={plan.features} />
          </div>

          {/* col-reverse on phones so the primary action sits above "Skip this"
              despite coming second in the DOM. */}
          <div className="flex w-full flex-col-reverse items-stretch gap-3 pt-4 sm:flex-row sm:items-center sm:justify-end sm:gap-4 sm:pt-6">
            <button
              type="button"
              onClick={onSkip}
              className="px-4 py-3 font-inter text-sm font-bold text-white transition-opacity hover:opacity-80"
            >
              Skip this
            </button>
            <button
              type="submit"
              disabled={!years}
              className="flex h-14 w-full items-center justify-center rounded-full bg-white px-4 font-suisse-intl text-xl leading-5 text-[#2A4775] transition-opacity hover:opacity-90 disabled:pointer-events-none disabled:opacity-50 sm:w-[120px]"
            >
              Next
            </button>
          </div>
        </div>

        <OrderSummaryCard summary={summary} className="lg:w-[402px] lg:shrink-0" />
      </div>
    </form>
  );
}
